// Premium type constants

export const PREMIUM_TYPE_MAP: Record<string, number> = {
  Set: 1,
  Select: 2,
  "Group Select": 3,
};

export const PREMIUM_TYPE_REVERSE_MAP: Record<number, string> = Object.fromEntries(
  Object.entries(PREMIUM_TYPE_MAP).map(([key, value]) => [value, key]),
) as Record<number, string>;

export function setSubConfigTypes(
  configType: string,
  hasGroupOrdering: boolean,
): { configType: string; chosenPackageTypeId: number } {
  if (configType === PREMIUM_TYPE_REVERSE_MAP[2] && hasGroupOrdering) {
    return {
      configType: PREMIUM_TYPE_REVERSE_MAP[3],
      chosenPackageTypeId: PREMIUM_TYPE_MAP.Select,
    };
  }
  if (configType === PREMIUM_TYPE_REVERSE_MAP[2]) {
    return {
      configType: PREMIUM_TYPE_REVERSE_MAP[2],
      chosenPackageTypeId: PREMIUM_TYPE_MAP.Select,
    };
  }
  return {
    configType: PREMIUM_TYPE_REVERSE_MAP[1],
    chosenPackageTypeId: PREMIUM_TYPE_MAP.Set,
  };
}

export const PACKAGE_COLOR_MAP: Record<string, string> = {
  [PREMIUM_TYPE_REVERSE_MAP[1]]: "yellow",
  [PREMIUM_TYPE_REVERSE_MAP[2]]: "purple",
  [PREMIUM_TYPE_REVERSE_MAP[3]]: "pink",
};

export const STATUS_TYPES = Object.freeze({
  EMPTY: "Empty",
  EMPTY_SINGLE: "Empty Single",
  CHANGE_MAKER: "Change Maker",
  DRAFT: "Draft",
  CREATED: "Created",
  STARTED: "Started",
  UNSTARTED: "Unstarted",
  UNPAID: "Unpaid",
  ACCEPTED: "Accepted",
  ASSIGNED: "Assigned",
  PAST_CUTOFF: "Unavailable",
  INACTIVE: "Disabled",
  DELIVERED: "Delivered",
  CANCELLED: "Cancelled",
});

// Statuses where the order is finalised and can no longer accept edits or
// payments. Used to gate resume-payment / checkout affordances. Frozen — this
// list controls payment gating, so it must not be mutated at runtime.
//
// The past-cut-off case is gated on cutoff_date_time (useCutoffLock) /
// isPastActiveCutOff — NOT on the "Accepted" status. A single (ad hoc) order
// reaches "Accepted" the instant its first payment succeeds, well before cut-off,
// so listing it here would wrongly block legitimate pre-cut-off edits. Mirrors
// the backend gate on bug-780/delayed-stripe-refund-reconciliation. The statuses
// below are genuinely finalised for both group and non-group orders; PAST_CUTOFF
// ("Unavailable") is a locked state too.
export const LOCKED_ORDER_STATUSES: readonly string[] = Object.freeze([
  STATUS_TYPES.ASSIGNED,
  STATUS_TYPES.DELIVERED,
  STATUS_TYPES.CANCELLED,
  STATUS_TYPES.PAST_CUTOFF,
]);

export const STATUS_TEXT_MAP: Record<string, string> = {
  [STATUS_TYPES.EMPTY]: "Awaiting",
  [STATUS_TYPES.EMPTY_SINGLE]: "Awaiting",
  [STATUS_TYPES.CHANGE_MAKER]: "Awaiting",
  [STATUS_TYPES.DRAFT]: "Confirm",
  [STATUS_TYPES.CREATED]: "In progress",
  [STATUS_TYPES.STARTED]: "In progress",
  [STATUS_TYPES.UNSTARTED]: "In progress",
  [STATUS_TYPES.UNPAID]: "In progress",
  [STATUS_TYPES.ACCEPTED]: "Scheduled",
  [STATUS_TYPES.ASSIGNED]: "Scheduled",
  [STATUS_TYPES.PAST_CUTOFF]: "Unavailable",
  [STATUS_TYPES.INACTIVE]: "Disabled",
  [STATUS_TYPES.DELIVERED]: "Delivered",
  [STATUS_TYPES.CANCELLED]: "Cancelled",
};

export const STATUS_OVERVIEW_ACTION_TYPES_MAP: Record<string, string> = {
  [STATUS_TYPES.EMPTY]: "changeMaker",
  [STATUS_TYPES.EMPTY_SINGLE]: "createOrder",
  [STATUS_TYPES.CHANGE_MAKER]: "changeMaker",
  [STATUS_TYPES.DRAFT]: "confirmMaker",
  [STATUS_TYPES.CREATED]: "editOrder",
  [STATUS_TYPES.STARTED]: "showOrder",
  [STATUS_TYPES.UNSTARTED]: "editOrder",
  [STATUS_TYPES.UNPAID]: "payOrder",
  [STATUS_TYPES.ACCEPTED]: "viewOrder",
  [STATUS_TYPES.ASSIGNED]: "viewOrder",
  [STATUS_TYPES.PAST_CUTOFF]: "show",
  [STATUS_TYPES.INACTIVE]: "show",
  [STATUS_TYPES.DELIVERED]: "viewOrder",
  [STATUS_TYPES.CANCELLED]: "show",
};

export const STATUS_CHANGE_ACTION_TYPES = {
  SELECT: "selectMaker",
  VIEW: "viewMenu",
  // Menu customisation (group orders): opens the manager's curate screen for this maker.
  CURATE: "curateMenu",
  SHOW: "showOrder",
  EDIT: "editOrder",
  CHANGE: "changeMaker",
} as const;

export const STATUS_CHANGE_ACTION_TYPES_MAP: Record<string, string> = {
  [STATUS_TYPES.EMPTY]: "selectMaker",
  [STATUS_TYPES.EMPTY_SINGLE]: "selectMaker",
  [STATUS_TYPES.CHANGE_MAKER]: "selectMaker",
  [STATUS_TYPES.DRAFT]: "editOrder",
  [STATUS_TYPES.CREATED]: "viewMenu",
  [STATUS_TYPES.STARTED]: "showOrder",
  [STATUS_TYPES.UNSTARTED]: "showOrder",
  [STATUS_TYPES.UNPAID]: "showOrder",
  [STATUS_TYPES.ACCEPTED]: "showOrder",
  [STATUS_TYPES.ASSIGNED]: "showOrder",
  [STATUS_TYPES.PAST_CUTOFF]: "showOrder",
  [STATUS_TYPES.INACTIVE]: "showOrder",
  [STATUS_TYPES.DELIVERED]: "showOrder",
  [STATUS_TYPES.CANCELLED]: "showOrder",
};

export const STATUS_ACTION_TEXT_MAP: Record<string, string> = {
  [STATUS_TYPES.EMPTY]: "Select maker before:",
  [STATUS_TYPES.EMPTY_SINGLE]: "Start a single order.",
  [STATUS_TYPES.CHANGE_MAKER]: "Place order before:",
  [STATUS_TYPES.DRAFT]: "Confirm maker before:",
  [STATUS_TYPES.CREATED]: "Place order before:",
  [STATUS_TYPES.UNSTARTED]: "Place order before:",
  [STATUS_TYPES.STARTED]: "Edit order before:",
  [STATUS_TYPES.UNPAID]: "Arriving:",
  [STATUS_TYPES.ACCEPTED]: "Arriving:",
  [STATUS_TYPES.ASSIGNED]: "Arriving:",
  [STATUS_TYPES.PAST_CUTOFF]: "This day's cut off has passed.",
  [STATUS_TYPES.INACTIVE]: "Ordering is only permitted with one maker at a time.",
  [STATUS_TYPES.DELIVERED]: "Delivered on:",
  [STATUS_TYPES.CANCELLED]: "This order was cancelled",
};

export const STATUS_BUTTON_TEXT_MAP: Record<string, string> = {
  [STATUS_TYPES.EMPTY]: "Choose Maker",
  [STATUS_TYPES.EMPTY_SINGLE]: "Create Single Order",
  [STATUS_TYPES.CHANGE_MAKER]: "Choose Maker",
  [STATUS_TYPES.DRAFT]: "Confirm",
  [STATUS_TYPES.CREATED]: "Place Order",
  [STATUS_TYPES.UNSTARTED]: "Place Order",
  [STATUS_TYPES.STARTED]: "View Order",
  [STATUS_TYPES.UNPAID]: "Pay Now",
  [STATUS_TYPES.ACCEPTED]: "View Order",
  [STATUS_TYPES.ASSIGNED]: "View Order",
  [STATUS_TYPES.PAST_CUTOFF]: "Unavailable",
  [STATUS_TYPES.INACTIVE]: "Disabled",
  [STATUS_TYPES.DELIVERED]: "View Order",
  [STATUS_TYPES.CANCELLED]: "Cancelled",
};

export const STATUS_COLOR_MAP: Record<string, string> = {
  [STATUS_TYPES.EMPTY]: "pink",
  [STATUS_TYPES.EMPTY_SINGLE]: "pink",
  [STATUS_TYPES.CHANGE_MAKER]: "pink",
  [STATUS_TYPES.DRAFT]: "green",
  [STATUS_TYPES.CREATED]: "orange",
  [STATUS_TYPES.STARTED]: "transparent-orange",
  [STATUS_TYPES.UNSTARTED]: "orange",
  [STATUS_TYPES.UNPAID]: "transparent-orange",
  [STATUS_TYPES.ACCEPTED]: "transparent-green",
  [STATUS_TYPES.ASSIGNED]: "transparent-green",
  [STATUS_TYPES.PAST_CUTOFF]: "grey",
  [STATUS_TYPES.INACTIVE]: "grey",
  [STATUS_TYPES.DELIVERED]: "transparent-green",
  [STATUS_TYPES.CANCELLED]: "red",
};

// Matched against `Product.unified_category` verbatim, so every entry must be the
// backend enum's exact spelling — including "Confectionary", which the enum
// misspells. Do not "correct" it here: the label the customer reads comes from
// CATEGORY_OPTIONS, and a mismatch silently drops every dish in the category
// from the whole Group Select menu with no error anywhere. That was the state
// until 2026-09-02, which is why no confectionery dish had ever appeared.
export const GROUP_SELECT_PRODUCT_CATEGORIES = [
  "Individual Meals",
  "Individual Handhelds",
  "Individual Snacks and Finger Food",
  "Individual Desserts",
  "Individual Breakfast",
  "Canapes",
  "Drinks",
  "Sauces & Toppings",
  "Confectionary",
  "Extras",
];

export function STATUS_PROP_VALIDATOR(val: string): boolean {
  return Object.values(STATUS_TYPES).includes(
    val as (typeof STATUS_TYPES)[keyof typeof STATUS_TYPES],
  );
}

export function PACKAGE_PROP_VALIDATOR(val: number | null): boolean {
  return val === null || Object.values(PREMIUM_TYPE_MAP).includes(val);
}

export const USER_ORDER_PAYMENT_STATUS = Object.freeze({
  FRESH: "fresh",
  AWAITING_PAYMENT_DETAILS: "awaitingPaymentDetails",
  PENDING: "pending",
  FAILED: "failed",
  COMPLETE: "complete",
  REFUND_PENDING: "refundPending",
});

// Convenience aliases used by MakerCard components
export const STATUS_TEXT = STATUS_TEXT_MAP;
export const STATUS_COLOR = STATUS_COLOR_MAP;
export const STATUS_BUTTON = STATUS_BUTTON_TEXT_MAP;
export const STATUS_ACTION = STATUS_CHANGE_ACTION_TYPES_MAP;
